import {Button, Spinner} from "react-bootstrap";
import type {TurnoverWorkbookData} from './turnoverWorkbook';
import FileSaver from "file-saver";
import {useTranslation} from "react-i18next";
import {TurnoverFilter, User} from "../models/table-models";
import dateFormat from "dateformat";
import {date} from "../utils/Enums/Common";
import {TableHeaders} from "../utils/Enums/TableHeaders";
import {getLetter} from "../utils/helper-functions";
import {useGetAllDocuments, getTurnoverExport} from "../utils/users-hooks";
import {Pagination} from "../utils/pagination-hooks";
import {getDocumentsPDF} from "../utils/clients-hooks";
import {toast} from "react-toastify";
import {CustomToastContent} from "../components/CustomToastContent";
import {toastOptionsTopRight} from "../utils/Enums/ToastOptions";
import React, {useState} from "react";

const generateWorkbook = (data: TurnoverWorkbookData): Promise<ArrayBuffer> => new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./turnoverWorkbook.worker.ts', import.meta.url));
    worker.onmessage = ({data}: MessageEvent<{buffer?: ArrayBuffer; error?: string}>) => {
        worker.terminate();
        if (data.buffer) resolve(data.buffer);
        else reject(new Error(data.error ?? 'Workbook generation failed'));
    };
    worker.onerror = (event) => {
        worker.terminate();
        reject(new Error(event.message));
    };
    worker.postMessage(data);
});

export const ExportTurnovers = ({pagination, turnoversFilter, fileName, wscols, users}: {
    pagination: Pagination,
    turnoversFilter: TurnoverFilter,
    fileName: string,
    wscols: TableHeaders[],
    users: User[]
}) => {
    const {t} = useTranslation('translation', {keyPrefix: 'table'});
    const {t: common} = useTranslation('translation', {keyPrefix: 'common'});
    const {t: invoiceT} = useTranslation('translation', {keyPrefix: 'invoice'});
    const {t: openingT} = useTranslation('translation', {keyPrefix: 'openingBalances'});
    const [loading,setLoading]=useState(false);
    const [loadingExcel, setLoadingExcel] = useState(false);

    const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";

    const headers = [...wscols.map((col) => t(col))];

    const useExportToCSV = async () => {
        setLoadingExcel(true);
        try {
        const response = await getTurnoverExport(turnoversFilter);
        const fetchTurnovers = response.summary, csvData = response.rows;
        const userNames = new Map(users.map(user => [user.id, user.name]));

        const formattedData = csvData.map((item) => [
            item.openingBalanceId ? openingT('turnoverSource') : item.documentFailType
                ? invoiceT(item.documentFailType.toLowerCase()) : getLetter(item.cash, item.documentType ?? undefined),
            item.number,
            item.documentType?.toLowerCase() === "receipt" ? "" : item.branchName,
            item.documentType?.toLowerCase() === "receipt" ? t("privatePerson") : item.companyName,
            item.documentType?.toLowerCase() === "receipt" ? "" : item.eik,
            item.responsibleUserIds != null
                ? item.responsibleUserIds.map(id => userNames.get(id) ?? String(id)).join(', ') || '—'
                : item.userId == null ? '—' : userNames.get(item.userId) ?? '—',
            item.recordedBy == null ? '—' : userNames.get(item.recordedBy) ?? '—',
            item.deviceCount,
            item.fromDate ? dateFormat(item.fromDate, date) : '',
            item.duePaymentDate ? dateFormat(item.duePaymentDate, date) : '',
            // Както в таблицата: датата на фактурата, не на задължението.
            item.openingBalanceId ? `${dateFormat(item.invoiceDate ?? undefined, date)} (${openingT('receiptInvoiceDate', { date: dateFormat(item.openingInvoiceDate ?? undefined, date) })})`
                : dateFormat(item.invoiceDate ?? undefined, date),
            Number((item.cash / 100).toFixed(2)),
            item.unpaidBank ? common('bankUnpaid') : item.paymentMethod ? common(item.paymentMethod.toLowerCase()) : '',
            item.taskType ? common(item.taskType) : ''
        ]);

        // Постъпилото и дължимото се сумират поотделно: събрани в един ред таблицата би казала, че
        // парите по неплатените фактури са влезли.
        const isUnpaidRow = (index: number) => csvData[index]?.unpaidBank;
        const totalInvoice = formattedData.reduce((sum, row, index) => {
            const value = typeof row[11] === "number" && !isUnpaidRow(index) && !csvData[index].openingBalanceId ? row[11] : 0;
            return sum + value;
        }, 0);
        const totalUnpaidBank = formattedData.reduce((sum, row, index) => {
            const value = typeof row[11] === "number" && isUnpaidRow(index) && !csvData[index].openingBalanceId ? row[11] : 0;
            return sum + value;
        }, 0);

        const totalDevices = formattedData.reduce((sum, row) => {
            const value = typeof row[7] === "number" ? row[7] : 0; // Ensure it's a number
            return sum + value;
        }, 0);

        const totalRow = [
            "", "", "", "", "", "", "", common("total") + ": " + totalDevices, "", "", "", common("total") + ": " + (totalInvoice + totalUnpaidBank).toFixed(2),
        ];
        formattedData.push(totalRow); // Append total row at the end
        formattedData.push(['', '', '', '', '', '', '', '', '', '', '', t('totalPaid') + ': ' + totalInvoice.toFixed(2)]);
        if (totalUnpaidBank > 0) {
            formattedData.push([
                "", "", "", "", "", "", "", "", "", "", "", t("unpaidBankPeriodSum") + ": " + totalUnpaidBank.toFixed(2),
            ]);
        }

        for (const [label, value] of [
            ['paidBankPeriodSum', fetchTurnovers.paidBankSum],
            ['paidBankCurrentSum', fetchTurnovers.paidBankCurrentSum],
            ['unpaidBankCurrentSum', fetchTurnovers.unpaidBankCurrentSum],
        ] as const) {
            formattedData.push(['', '', '', '', '', '', '', '', '', '', '',
                t(label) + ': ' + ((value ?? 0) / 100).toFixed(2)]);
        }

        const totalPaidOpening = csvData.reduce((sum, row) => sum +
            (!row.unpaidBank && row.openingBalanceId ? row.cash / 100 : 0), 0);
        if (totalPaidOpening !== 0) formattedData.push([
            '', '', '', '', '', '', '', '', '', '', '', t('paidOpeningSum') + ': ' + totalPaidOpening.toFixed(2),
        ]);
        const number = (value: number) => (value ?? 0) / 100;
        const summary = fetchTurnovers.turnovers.map(row => [
            row.userId == null ? common('unassignedTurnover') : users.find(user => user.id === row.userId)?.name ?? '—',
            number(row.currentSum), number(row.openingSum), number(row.sum)
        ]);
        summary.push([String(common('total')), ...[1, 2, 3].map(column =>
            summary.reduce((sum, row) => sum + Number(row[column]), 0))]);
        const excelBuffer = await generateWorkbook({headers, rows: formattedData, summary: [
            [common('turnoverByUsers'), openingT('currentDocuments'), openingT('openingReceipts'), common('total')].map(String),
            ...summary
        ], summaryName: common('turnoverByUsers')});
        const data = new Blob([excelBuffer], {type: fileType});
        FileSaver.saveAs(data, fileName + fileExtension);

        } catch {
            toast.error(<CustomToastContent content={[`${t('exportError')}`]} whiteText/>, toastOptionsTopRight);
        } finally {
            setLoadingExcel(false);
        }
    };

    const usePrint = async () => {
        setLoading(true);

        const fetchDocuments = await useGetAllDocuments({
            ...pagination,
            currentPage: 1,
            pageSize: 1000000
        }, {...turnoversFilter}), csvData = fetchDocuments.items;

        if (csvData.length > 0) {
            const pdfFile = await getDocumentsPDF(
                csvData
            );
            if (pdfFile) {
                const pdf = URL.createObjectURL(pdfFile);
                window.open(pdf, '_blank');
                setLoading(false);
                return true;
            } else {
                toast.error(<CustomToastContent content={[`${t('printError')}`]} whiteText/>, toastOptionsTopRight);
                setLoading(false);
                return false;
            }
        }
        setLoading(false);
    };

    return (
        <div className={"d-flex flex-column align-items-center"}>
            {loadingExcel ? <Spinner animation={'border'}></Spinner>
                :
                <Button
                    variant="warning"
                    className={"mb-2"}
                    onClick={useExportToCSV}
                >
                    {common('exportExcel')}
                </Button>}
            {/*{loading ? <Spinner animation={'border'}></Spinner>*/}
            {/*    :*/}
            {/*    <Button*/}
            {/*        variant="warning"*/}
            {/*        className={"mb-2"}*/}
            {/*        onClick={usePrint}*/}
            {/*    >*/}
            {/*        {common('printAllInvoices')}*/}
            {/*    </Button>}*/}
        </div>
    );
};
